import { useAvailableRoomBookingState } from "./hooks/useAvailableRoomBookingState";

import { useAppSelector, useModal } from "@hooks";
import { MeetingRoomBookingDetailsContent, ModalPageBottom } from "@organisms";
import {
  MeetingRoom,
  getIsOnlineMeetingLinkSupported,
  markAsBookedLocally,
  useAppDispatch,
  Timeslot,
} from "@lib/store";
import { isSameDay, parseISO } from "date-fns";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useCreateEvent, useDestructiveDialog, useFormatDate } from "@hooks";
import { StyledModalPage } from "./styles";
import { AvailableRoomAttendeesModalPage } from "./AvailableRoomAttendeesModalPage";
import { FlexCol } from "@quarks";

export const AvailableRoomBookingDetailsModalPage = ({
  onBack,
  onClose,
  room,
  dynamicTimeslot,
}: {
  dynamicTimeslot?: Timeslot;
  onBack: () => void;
  onClose: () => void;
  room: MeetingRoom;
}) => {
  const bookingState = useAvailableRoomBookingState(room);

  const { addPages, removePages } = useModal();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { destructiveDialog } = useDestructiveDialog();

  const onlineMeetingLinkSupported = useAppSelector(getIsOnlineMeetingLinkSupported);
  const { invitees, isOnlineMeeting, message, setInvitees, setIsOnlineMeeting, setMessage, setTitle, title } =
    bookingState;
  const email = room.email;

  const dynamicStartString = dynamicTimeslot?.start?.toISOString() ?? "";
  const dynamicEndString = dynamicTimeslot?.end?.toISOString() ?? "";
  const endDateFormat = isSameDay(parseISO(dynamicStartString), parseISO(dynamicEndString)) ? "HH:mm" : "dd MMMM HH:mm";
  const confirmationMessageStartDateTime = useFormatDate(dynamicStartString, "dd MMMM HH:mm");
  const confirmationMessageEndDateTime = useFormatDate(dynamicEndString, endDateFormat);

  const hasValidTimeslot = !!dynamicStartString && !!dynamicEndString;

  const handleOnSuccess = useCallback(() => {
    dispatch(markAsBookedLocally({ buildingId: room.buildingId, roomId: room.id }));
    onClose();
  }, [dispatch, onClose, room.buildingId, room.id]);

  const { createEvent, isCreatingEvent } = useCreateEvent(handleOnSuccess);
  const hasUnsavedChanges =
    title !== room.displayName ||
    message !== "" ||
    invitees.length > 0 ||
    (onlineMeetingLinkSupported && !isOnlineMeeting);

  const resetFormState = useCallback(() => {
    setMessage("");
    setTitle(room.displayName);
    setIsOnlineMeeting(true);
    setInvitees([]);
  }, [room.displayName, setInvitees, setIsOnlineMeeting, setMessage, setTitle]);

  const handleBackFromBookingDetails = useCallback(async () => {
    if (!hasUnsavedChanges) {
      onBack();
      return;
    }

    const confirmation = await destructiveDialog(
      t("meeting:DiscardEventChangesTitle"),
      t("meeting:DiscardEventChangesMessage"),
      "warning",
      t("meeting:DiscardEventChangesConfirm"),
    );

    if (confirmation) {
      resetFormState();
      onBack();
    }
  }, [destructiveDialog, hasUnsavedChanges, onBack, resetFormState, t]);

  const handleClose = useCallback(async () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    const confirmation = await destructiveDialog(
      t("meeting:DiscardEventChangesTitle"),
      t("meeting:DiscardEventChangesMessage"),
      "warning",
      t("meeting:DiscardEventChangesConfirm"),
    );

    if (confirmation) {
      onClose();
    }
  }, [destructiveDialog, hasUnsavedChanges, onClose, t]);

  return (
    <StyledModalPage
      onBack={handleBackFromBookingDetails}
      onClose={handleClose}
      title={t("screen:MeetingRoomDetails")}
      bottom={
        <FlexCol
          gap={16}
          className="px-[var(--padding-x)] pt-4 border-t">
          <ModalPageBottom
            className="p-0 "
            buttonLabel={t("meeting:CreateMeetingCreateEventButton")}
            buttonDisabled={!title || !hasValidTimeslot}
            buttonLoading={isCreatingEvent}
            onClick={() =>
              createEvent(
                {
                  title,
                  message,
                  attendees: invitees.map(attendee => attendee.email),
                  startDateTime: dynamicStartString,
                  endDateTime: dynamicEndString,
                  isOnlineMeeting,
                  rooms: [email],
                  isAllDay: false,
                  organizerTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  origin: "BookRoomNow",
                },
                t("meeting:CreateMeetingSuccessMessage", {
                  startDate: confirmationMessageStartDateTime,
                  endDate: confirmationMessageEndDateTime,
                }),
              )
            }
          />
        </FlexCol>
      }>
      <MeetingRoomBookingDetailsContent
        attendees={invitees}
        dynamicTimeslot={dynamicTimeslot}
        isOnlineMeeting={isOnlineMeeting}
        message={message}
        onInviteesClick={() => {
          addPages([
            <AvailableRoomAttendeesModalPage
              bookingState={bookingState}
              onBack={() => removePages(1)}
              onClose={handleClose}
            />,
          ]);
        }}
        onIsOnlineMeetingChange={setIsOnlineMeeting}
        onMessageChange={setMessage}
        onTitleChange={setTitle}
        room={room}
        title={title}
      />
    </StyledModalPage>
  );
};
